// Lote de preventivas do mês: todas as visitas previstas pelos planos dos contratos, com e sem OS.
// O mês corrente e (a partir do dia 20) o seguinte são gerados sozinhos de manhã; o botão gera na hora.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { ChevronLeft, ChevronRight, Search } from 'lucide-react';
import { api } from '../../api';
import { pode, useSessao } from '../../sessao';
import { SISTEMAS, dataBr, type Visita } from './comum';

interface Lote {
  competencia: string; nome: string; anterior: string; seguinte: string; lote_dia: number; gera_sozinho: boolean;
  resumo: { previstas: number; criadas: number; agendadas: number; puladas: number }; visitas: Visita[];
}
const NOME_STATUS: Record<string, string> = { aberta: 'a agendar', agendada: 'agendada', em_deslocamento: 'a caminho', em_execucao: 'em execução', pausada: 'pausada', concluida: 'concluída', cancelada: 'cancelada' };

export function Preventivas() {
  const { usuario } = useSessao();
  const podeAgendar = pode(usuario, 'os.agendar');
  const [params, setParams] = useSearchParams();
  const [lote, setLote] = useState<Lote | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [busca, setBusca] = useState('');
  const [filtro, setFiltro] = useState<'todas' | 'sem_os' | 'a_agendar' | 'agendadas'>('todas');
  const [gerando, setGerando] = useState(false);
  const mes = params.get('mes');

  const carregar = useCallback(() => {
    api<Lote>(`/preventivas${mes ? `?competencia=${mes}` : ''}`).then(setLote).catch((e) => setErro(e.message));
  }, [mes]);
  useEffect(() => { carregar(); }, [carregar]);

  const linhas = useMemo(() => {
    if (!lote) return [];
    const b = busca.trim().toLowerCase();
    return lote.visitas.filter((v) => (!b || `${v.cliente} ${v.local} ${v.contrato} ${v.plano}`.toLowerCase().includes(b))
      && (filtro === 'todas' || (filtro === 'sem_os' && !v.os_id && !v.pulada) || (filtro === 'a_agendar' && v.os_id && !v.agendada_para && v.os_status === 'aberta') || (filtro === 'agendadas' && v.agendada_para)))
      .sort((a, b2) => a.data_sugerida.localeCompare(b2.data_sugerida) || a.cliente.localeCompare(b2.cliente));
  }, [lote, busca, filtro]);

  async function gerar() {
    setErro(null); setOk(null); setGerando(true);
    try {
      const r = await api<{ criadas: number }>('/preventivas/gerar', { metodo: 'POST', corpo: { competencia: lote!.competencia.slice(0, 7) } });
      setOk(r.criadas ? `${r.criadas} OS criada(s) "a agendar". Ponha as datas na Agenda.` : 'Nada novo para criar.'); carregar();
    } catch (e) { setErro((e as Error).message); } finally { setGerando(false); }
  }
  const hoje = new Date(Date.now() - 3 * 3600_000).toISOString().slice(0, 7);

  return (
    <>
      <div className="cabecalho-pagina">
        <div><span className="kicker">Contratos</span><h1 className="titulo">Preventivas {lote ? `de ${lote.nome}` : ''}</h1></div>
        {lote && (
          <div className="acoes-cabecalho">
            <button className="btn-fantasma" onClick={() => setParams({ mes: lote.anterior })} aria-label="Mês anterior"><ChevronLeft size={16} aria-hidden /></button>
            <button className="btn-fantasma" onClick={() => setParams({})} disabled={!mes || mes === hoje}>Este mês</button>
            <button className="btn-fantasma" onClick={() => setParams({ mes: lote.seguinte })} aria-label="Próximo mês"><ChevronRight size={16} aria-hidden /></button>
            {podeAgendar && lote.competencia.slice(0, 7) >= hoje && <button className="btn" onClick={gerar} disabled={gerando || !lote.visitas.some((v) => !v.os_id && !v.pulada)}>{gerando ? 'Gerando…' : `Gerar OS de ${lote.nome}`}</button>}
          </div>
        )}
      </div>
      <p className="nota">Visitas previstas pelo plano de cada contrato. {lote?.gera_sozinho ? 'Este mês é gerado sozinho toda manhã.' : `O lote do mês seguinte sai sozinho a partir do dia ${lote?.lote_dia ?? 20}.`} A data sugerida espalha as visitas no mês; a data real se põe na <Link to="/agenda">Agenda</Link>.</p>
      {erro && <p className="alerta" role="alert">{erro}</p>}
      {ok && <p className="alerta alerta-ok" role="status">{ok}</p>}
      {lote && (
        <>
          <div className="numeros">
            <div className="cartao numero"><strong>{lote.resumo.previstas}</strong><span>previstas</span></div>
            <div className="cartao numero"><strong>{lote.resumo.criadas}</strong><span>com OS</span></div>
            <div className="cartao numero"><strong>{lote.resumo.agendadas}</strong><span>agendadas</span></div>
            {lote.resumo.puladas > 0 && <div className="cartao numero"><strong>{lote.resumo.puladas}</strong><span>janela passou</span></div>}
          </div>
          <div className="barra-filtros">
            <label className="campo-busca"><Search size={18} strokeWidth={1.7} aria-hidden /><input type="search" placeholder="Cliente, parque ou contrato" value={busca} onChange={(e) => setBusca(e.target.value)} aria-label="Buscar" /></label>
            <div className="segmentos" role="group" aria-label="Filtro">
              {([['todas', 'Todas'], ['sem_os', 'Sem OS'], ['a_agendar', 'A agendar'], ['agendadas', 'Agendadas']] as const).map(([v, r]) => (
                <button key={v} className={filtro === v ? 'ativo' : ''} onClick={() => setFiltro(v)}>{r}</button>
              ))}
            </div>
          </div>
          {linhas.length === 0 ? <div className="cartao vazio"><p>{lote.visitas.length ? 'Nada com esse filtro.' : 'Nenhuma visita prevista neste mês. Os planos ficam na ficha de cada contrato.'}</p></div> : (
            <div className="cartao tabela-cartao rolagem-x"><table className="tabela">
              <thead><tr><th>Sugerida</th><th>Cliente / parque</th><th>Linha do plano</th><th>Visita</th><th>OS</th></tr></thead>
              <tbody>{linhas.map((v) => (
                <tr key={`${v.plano_id}-${v.local_id}-${v.visita}`} className={v.pulada || v.os_status === 'cancelada' ? 'inativo' : ''}>
                  <td>{dataBr(v.data_sugerida)}</td>
                  <td><Link to={`/contratos/${v.contrato_id}`}><strong>{v.cliente}</strong></Link><small>{v.local} · {v.contrato}</small></td>
                  <td>{v.plano}<small>{v.sistemas.length ? v.sistemas.map((s) => SISTEMAS[s]).join(', ') : 'todos os sistemas'}{v.duracao_min ? ` · ${v.duracao_min} min` : ''}</small></td>
                  <td>{v.de > 1 ? `${v.visita} de ${v.de}` : '—'}</td>
                  <td>{v.os_id ? <><Link to={`/os/${v.os_id}`}>{v.os_numero}</Link><small>{v.agendada_para ? `agendada ${new Date(v.agendada_para).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}` : NOME_STATUS[v.os_status ?? '']}</small></>
                    : <small>{v.pulada ?? 'ainda não gerada'}</small>}</td>
                </tr>
              ))}</tbody>
            </table></div>
          )}
        </>
      )}
    </>
  );
}
